import { ApiError, getLocationCompConfig, updateLocationCompConfig } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { CompConfigResponse } from '@jq4race/shared';
import { Button, Card, CardContent } from '@jq4race/ui';
import { useParams } from '@tanstack/react-router';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { formatDateTime } from '../../../../shared/format/format-date';
import { PageHeader } from '../../../app-shell/ui/PageHeader';

// ── Component ─────────────────────────────────────────────────────────────────

export function CompConfigPage() {
  const { t, i18n } = useTranslation('location-comp-config');
  const { locationId } = useParams({ strict: false });

  const [config, setConfig] = useState<CompConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [compsEnabled, setCompsEnabled] = useState(false);
  const [supervisorAuthorized, setSupervisorAuthorized] = useState(false);
  const [operatorAuthorized, setOperatorAuthorized] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const response = await getLocationCompConfig(locationId);
      setConfig(response);
      setCompsEnabled(response.compsEnabled);
      setSupervisorAuthorized(response.compAuthorizedRoles.includes('supervisor'));
      setOperatorAuthorized(response.compAuthorizedRoles.includes('operator'));
    } catch (e) {
      setErrorBanner(translateApiError(e, t, { fallback: t('feedback.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [locationId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-dismiss success banner
  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => setSuccess(null), 5000);
    return () => window.clearTimeout(id);
  }, [success]);

  // When comps are disabled, authorized roles must be empty (domain invariant).
  // We reset role state visually when toggling comps off so the user sees the
  // consequence immediately; the actual body sent on submit enforces this.
  function handleCompsEnabledChange(checked: boolean) {
    setCompsEnabled(checked);
    if (!checked) {
      setSupervisorAuthorized(false);
      setOperatorAuthorized(false);
    }
  }

  // Client-side guard: comps enabled but no role selected → reject before sending.
  const rolesEmpty = compsEnabled && !supervisorAuthorized && !operatorAuthorized;
  const saveDisabled = submitting || rolesEmpty;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!locationId || saveDisabled) return;
    setSubmitting(true);
    setErrorBanner(null);

    const compAuthorizedRoles: ('supervisor' | 'operator')[] = [];
    if (compsEnabled) {
      if (supervisorAuthorized) compAuthorizedRoles.push('supervisor');
      if (operatorAuthorized) compAuthorizedRoles.push('operator');
    }

    try {
      const saved = await updateLocationCompConfig(locationId, {
        compsEnabled,
        compAuthorizedRoles,
      });
      setConfig(saved);
      setCompsEnabled(saved.compsEnabled);
      setSupervisorAuthorized(saved.compAuthorizedRoles.includes('supervisor'));
      setOperatorAuthorized(saved.compAuthorizedRoles.includes('operator'));
      setSuccess(t('feedback.saveSuccess'));
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorBanner(translateApiError(e, t, { fallback: t('feedback.saveError') }));
      } else {
        setErrorBanner(translateApiError(e, t, { fallback: t('feedback.unexpectedError') }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      {errorBanner && (
        <FeedbackBanner tone="error" message={errorBanner} onDismiss={() => setErrorBanner(null)} />
      )}
      {success && (
        <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />
      )}

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {loading ? (
            <p className="text-[13px] text-jq-graphite">{t('loading')}</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* ── Master toggle ── */}
              <fieldset className="space-y-4">
                <legend className="text-[13px] font-medium text-jq-graphite">
                  {t('form.permissionsLegend')}
                </legend>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="field-comps-enabled"
                    checked={compsEnabled}
                    onChange={(e) => handleCompsEnabledChange(e.target.checked)}
                    className="h-4 w-4 rounded accent-jq-primary"
                  />
                  <label htmlFor="field-comps-enabled" className="text-[13px] text-jq-graphite">
                    {t('form.allowComps')}
                  </label>
                </div>
              </fieldset>

              {/* ── Role checkboxes (only visible when comps are enabled) ── */}
              {compsEnabled && (
                <fieldset className="space-y-4 border-l-2 border-jq-sand/60 pl-4">
                  <legend className="text-[13px] font-medium text-jq-graphite">
                    {t('form.rolesLegend')}
                  </legend>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="field-role-supervisor"
                      checked={supervisorAuthorized}
                      onChange={(e) => setSupervisorAuthorized(e.target.checked)}
                      className="h-4 w-4 rounded accent-jq-primary"
                    />
                    <label htmlFor="field-role-supervisor" className="text-[13px] text-jq-graphite">
                      {t('form.roleSupervisor')}
                    </label>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="field-role-operator"
                      checked={operatorAuthorized}
                      onChange={(e) => setOperatorAuthorized(e.target.checked)}
                      className="h-4 w-4 rounded accent-jq-primary"
                    />
                    <label htmlFor="field-role-operator" className="text-[13px] text-jq-graphite">
                      {t('form.roleOperator')}
                    </label>
                  </div>

                  {rolesEmpty && (
                    <p className="text-[12px] text-jq-danger">{t('form.rolesRequired')}</p>
                  )}
                </fieldset>
              )}

              {config?.updatedAt && (
                <p className="text-[12px] text-jq-graphite/60">
                  {t('form.lastUpdated', { date: formatDateTime(config.updatedAt, i18n.language) })}
                </p>
              )}

              <Button type="submit" disabled={saveDisabled}>
                {submitting ? t('form.saving') : t('form.save')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('location-comp-config');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
